// An open tab picks up a new deploy by itself. A tab loaded before a deploy
// keeps running the old bundle against the new Worker, and its controls can
// send messages the new Room ignores (QUEUE row 239: a "Got it" that did
// nothing). So the page asks for its own index.html now and then, and when the
// bundle it names has changed, reloads, but only while nothing would be lost:
// no call on screen, nobody typing, and not on a page he edits.
//
// It asks when the tab comes back into view and every five minutes while it is
// visible: one small request, never a loop.

const EVERY_MS = 5 * 60 * 1000;
/** A tab that just came into view is not asked again sooner than this. */
const MIN_GAP_MS = 30 * 1000;

function bundleOf(html: string): string | null {
  return html.match(/\/assets\/index-[A-Za-z0-9_-]+\.js/)?.[0] ?? null;
}

function runningBundle(): string | null {
  for (const s of Array.from(document.querySelectorAll<HTMLScriptElement>("script[src]"))) {
    const found = bundleOf(s.getAttribute("src") ?? "");
    if (found) return found;
  }
  return null;
}

function typing(): boolean {
  const el = document.activeElement as HTMLElement | null;
  if (!el) return false;
  return el.isContentEditable || el.tagName === "INPUT" || el.tagName === "TEXTAREA" || el.tagName === "SELECT";
}

/** `idle` says whether a reload would lose nothing on the page shown now. */
export function watchForNewDeploy(idle: () => boolean): () => void {
  const running = runningBundle();
  // The dev server has no hashed bundle; nothing to compare.
  if (!running) return () => {};
  let lastAsked = 0;
  let stale = false;
  let stopped = false;

  const reloadIfIdle = (): void => {
    if (stale && !stopped && document.visibilityState === "visible" && idle() && !typing()) window.location.reload();
  };

  const ask = async (): Promise<void> => {
    if (stopped || document.visibilityState !== "visible") return;
    const now = Date.now();
    if (now - lastAsked < MIN_GAP_MS) return;
    lastAsked = now;
    if (!stale) {
      try {
        const res = await fetch(`/?fresh=${now}`, { cache: "no-store", credentials: "omit" });
        if (!res.ok) return;
        const live = bundleOf(await res.text());
        stale = live !== null && live !== running;
      } catch {
        return;
      }
    }
    reloadIfIdle();
  };

  const onVisible = (): void => void ask();
  document.addEventListener("visibilitychange", onVisible);
  // Typing stopped: a stale tab may reload now.
  const onBlur = (): void => {
    if (stale) setTimeout(reloadIfIdle, 1500);
  };
  document.addEventListener("focusout", onBlur);
  const timer = setInterval(() => void ask(), EVERY_MS);
  return () => {
    stopped = true;
    clearInterval(timer);
    document.removeEventListener("visibilitychange", onVisible);
    document.removeEventListener("focusout", onBlur);
  };
}
